/**
 * Centralized API configuration for CMS and shop services.
 *
 * Production falls back to the Forge domains.
 * Development defaults to local proxy paths to avoid browser CORS errors.
 */

const DEFAULT_CMS_API_URL = 'https://inversuncms.on-forge.com/api'
const DEFAULT_SHOP_API_URL = 'https://oliverlaravel.on-forge.com/api'
const DEFAULT_CMS_STORAGE_URL = 'https://inversuncms.on-forge.com/storage'
const DEFAULT_SHOP_STORAGE_URL = 'https://oliverlaravel.on-forge.com/storage'

const DEV_CMS_API_PATH = '/cms-api'
const DEV_SHOP_API_PATH = '/shop-api'
const DEV_CMS_STORAGE_PATH = '/cms-storage'
const DEV_SHOP_STORAGE_PATH = '/shop-storage'

const stripTrailingSlash = (value) => String(value || '').trim().replace(/\/+$/, '')
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || '').trim())
const isDevelopment = process.env.NODE_ENV === 'development'

const pickEnvValue = (...keys) => {
  for (const key of keys) {
    const value = process.env[key]
    if (value && value.trim()) {
      return value.trim()
    }
  }

  return ''
}

const buildEndpoint = (baseUrl, path = '') => {
  const normalizedBaseUrl = stripTrailingSlash(baseUrl)
  const normalizedPath = String(path || '').replace(/^\/+/, '')
  return normalizedPath ? `${normalizedBaseUrl}/${normalizedPath}` : normalizedBaseUrl
}

const resolveApiBaseUrl = ({ envKeys, devPath, fallbackUrl }) => {
  if (isDevelopment) {
    return devPath
  }

  const envValue = pickEnvValue(...envKeys)

  if (envValue) {
    return stripTrailingSlash(envValue)
  }

  return fallbackUrl
}

const resolveStorageBaseUrl = ({ envKeys, devPath, fallbackUrl, apiBaseUrl }) => {
  if (isDevelopment) {
    return devPath
  }

  const envValue = pickEnvValue(...envKeys)

  if (envValue) {
    return stripTrailingSlash(envValue)
  }

  if (isAbsoluteUrl(apiBaseUrl)) {
    return `${new URL(apiBaseUrl).origin}/storage`
  }

  return fallbackUrl
}

export const CMS_API_BASE_URL = resolveApiBaseUrl({
  envKeys: ['VUE_APP_CMS_API_URL', 'VUE_APP_API_URL'],
  devPath: DEV_CMS_API_PATH,
  fallbackUrl: DEFAULT_CMS_API_URL,
})

export const SHOP_API_BASE_URL = resolveApiBaseUrl({
  envKeys: ['VUE_APP_SHOP_API_URL'],
  devPath: DEV_SHOP_API_PATH,
  fallbackUrl: DEFAULT_SHOP_API_URL,
})

export const CMS_STORAGE_BASE_URL = resolveStorageBaseUrl({
  envKeys: ['VUE_APP_CMS_STORAGE_URL'],
  devPath: DEV_CMS_STORAGE_PATH,
  fallbackUrl: DEFAULT_CMS_STORAGE_URL,
  apiBaseUrl: CMS_API_BASE_URL,
})

export const SHOP_STORAGE_BASE_URL = resolveStorageBaseUrl({
  envKeys: ['VUE_APP_SHOP_STORAGE_URL'],
  devPath: DEV_SHOP_STORAGE_PATH,
  fallbackUrl: DEFAULT_SHOP_STORAGE_URL,
  apiBaseUrl: SHOP_API_BASE_URL,
})

// Default API URL (maintains backward compatibility)
export const API_BASE_URL = CMS_API_BASE_URL

// Health check
export const API_HEALTH_URL = buildEndpoint(API_BASE_URL, 'health')

/**
 * API Endpoints Configuration
 * All CMS API routes centralized for easy management
 */
export const apiEndpoints = {
  // Health
  health: buildEndpoint(API_BASE_URL, 'health'),
  
  // Products
  products: {
    list: buildEndpoint(API_BASE_URL, 'products'),
    featured: buildEndpoint(API_BASE_URL, 'products/featured'),
    dropdown: buildEndpoint(API_BASE_URL, 'products/dropdown'),
    detail: (idOrSlug) => buildEndpoint(API_BASE_URL, `products/${idOrSlug}`),
    variants: (idOrSlug) => buildEndpoint(API_BASE_URL, `products/${idOrSlug}/variants`),
    related: (idOrSlug) => buildEndpoint(API_BASE_URL, `products/${idOrSlug}/related`),
  },
  
  // Categories
  categories: {
    list: buildEndpoint(API_BASE_URL, 'categories'),
    detail: (slug) => buildEndpoint(API_BASE_URL, `categories/${slug}`),
    products: (slug) => buildEndpoint(API_BASE_URL, `categories/${slug}/products`),
  },
  
  // Inquiries & Forms
  inquiries: {
    order: buildEndpoint(API_BASE_URL, 'inquiries'),
    contact: buildEndpoint(API_BASE_URL, 'contact'),
    quote: buildEndpoint(API_BASE_URL, 'quote'),
  },
  
  // Media Library
  mediaList: buildEndpoint(API_BASE_URL, 'media'),
  mediaLibrary: buildEndpoint(API_BASE_URL, 'media/library'),
  mediaUpload: buildEndpoint(API_BASE_URL, 'media/upload'),
  
  // Settings & Content
  settings: {
    all: buildEndpoint(API_BASE_URL, 'settings'),
    byGroup: (group) => buildEndpoint(API_BASE_URL, `settings/${group}`),
  },
  
  // Homepage
  homepage: buildEndpoint(API_BASE_URL, 'homepage'),
  homepageData: buildEndpoint(API_BASE_URL, 'homepage-data'),
  
  // Testimonials
  testimonials: buildEndpoint(API_BASE_URL, 'testimonials'),

  // Support Documents
  supportDocuments: buildEndpoint(API_BASE_URL, 'support-documents'),

  // Theme
  theme: buildEndpoint(API_BASE_URL, 'theme'),

  // Pages (CMS)
  pages: {
    list: buildEndpoint(API_BASE_URL, 'pages'),
    detail: (slug) => buildEndpoint(API_BASE_URL, `pages/${slug}`),
  },
  
  // Projects/Portfolio
  projects: {
    list: buildEndpoint(API_BASE_URL, 'projects'),
    featured: buildEndpoint(API_BASE_URL, 'projects/featured'),
    detail: (slug) => buildEndpoint(API_BASE_URL, `projects/${slug}`),
  },

  // Gallery Images
  gallery: {
    list: buildEndpoint(API_BASE_URL, 'gallery'),
    byCollection: (collection) => buildEndpoint(API_BASE_URL, `gallery/${collection}`),
  },

  // Shop - Products (uses SHOP_API_BASE_URL)
  shopProducts: {
    list: buildEndpoint(SHOP_API_BASE_URL, 'products'),
    featured: buildEndpoint(SHOP_API_BASE_URL, 'products/featured'),
    detail: (idOrSlug) => buildEndpoint(SHOP_API_BASE_URL, `products/${idOrSlug}`),
    variants: (idOrSlug) => buildEndpoint(SHOP_API_BASE_URL, `products/${idOrSlug}/variants`),
    related: (idOrSlug) => buildEndpoint(SHOP_API_BASE_URL, `products/${idOrSlug}/related`),
  },

  // Shop - Categories (uses SHOP_API_BASE_URL)
  shopCategories: {
    list: buildEndpoint(SHOP_API_BASE_URL, 'categories'),
    detail: (slug) => buildEndpoint(SHOP_API_BASE_URL, `categories/${slug}`),
    products: (slug) => buildEndpoint(SHOP_API_BASE_URL, `categories/${slug}/products`),
  },

  // Shop - Cart (uses SHOP_API_BASE_URL)
  cart: {
    show: buildEndpoint(SHOP_API_BASE_URL, 'cart'),
    addItem: buildEndpoint(SHOP_API_BASE_URL, 'cart/items'),
    updateItem: (id) => buildEndpoint(SHOP_API_BASE_URL, `cart/items/${id}`),
    removeItem: (id) => buildEndpoint(SHOP_API_BASE_URL, `cart/items/${id}`),
    clear: buildEndpoint(SHOP_API_BASE_URL, 'cart'),
  },

  // Shop - Wishlist (uses SHOP_API_BASE_URL)
  wishlist: {
    show: buildEndpoint(SHOP_API_BASE_URL, 'account/wishlist'),
    addItem: buildEndpoint(SHOP_API_BASE_URL, 'account/wishlist'),
    removeItem: (itemId) => buildEndpoint(SHOP_API_BASE_URL, `account/wishlist/${itemId}`),
  },

  // Shop - Checkout (uses SHOP_API_BASE_URL)
  checkout: {
    checkout: buildEndpoint(SHOP_API_BASE_URL, 'checkout'),
  },

  // Shop - Account (uses SHOP_API_BASE_URL)
  account: {
    dashboard: buildEndpoint(SHOP_API_BASE_URL, 'account'),
    orders: buildEndpoint(SHOP_API_BASE_URL, 'account/orders'),
    order: (id) => buildEndpoint(SHOP_API_BASE_URL, `account/orders/${id}`),
    profile: buildEndpoint(SHOP_API_BASE_URL, 'account/profile'),
  },

  // Shop - Search
  search: buildEndpoint(SHOP_API_BASE_URL, 'search'),

  // Shop Customer Authentication (uses SHOP_API_BASE_URL)
  auth: {
    register: buildEndpoint(SHOP_API_BASE_URL, 'auth/register'),
    login: buildEndpoint(SHOP_API_BASE_URL, 'auth/login'),
    logout: buildEndpoint(SHOP_API_BASE_URL, 'auth/logout'),
    forgotPassword: buildEndpoint(SHOP_API_BASE_URL, 'auth/forgot-password'),
    resetPassword: buildEndpoint(SHOP_API_BASE_URL, 'auth/reset-password'),
    refresh: buildEndpoint(SHOP_API_BASE_URL, 'auth/refresh'),
    me: buildEndpoint(SHOP_API_BASE_URL, 'auth/me'),
  },
}

/**
 * Get full API URL with optional query parameters
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - Query parameters object
 * @returns {string} Full URL with query string
 */
export function getApiUrl(endpoint, params = {}) {
  const url = new URL(endpoint, window.location.origin)
  
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      url.searchParams.append(key, params[key])
    }
  })
  
  // Return just the pathname + search if it's same origin
  if (url.origin === window.location.origin) {
    return url.pathname + url.search
  }
  
  return url.toString()
}

/**
 * API Configuration object
 * Includes base URL, headers, timeout settings
 */
export const apiConfig = {
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // For CORS with credentials
}

export default {
  API_BASE_URL,
  CMS_API_BASE_URL,
  SHOP_API_BASE_URL,
  CMS_STORAGE_BASE_URL,
  SHOP_STORAGE_BASE_URL,
  API_HEALTH_URL,
  apiEndpoints,
  getApiUrl,
  apiConfig,
}
